
import { useEffect, useState } from 'react';
import { addSkill, deleteSkill, getSkills, updateSkill } from '../API/res';

const SkillsSection = () => {
  const [skills, setSkills] = useState([]);
  const [forms, setForms] = useState([]);
  const [editingSkillId, setEditingSkillId] = useState(null);
  const [originalSkill, setOriginalSkill] = useState(null);

  const fetchSkills = async () => {
    try {
      const response = await getSkills();
      setSkills(response.data);
    } catch (err) {
      console.error('Failed to fetch skills', err);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const handleAddForm = () => {
    setForms([
      ...forms,
      {
        skill_name: '',
        proficiency_level: 'beginner',
        certification_name: '',
        certification_issuer: '',
        years_of_experience: ''
      }
    ]);
  };

  const handleFormChange = (index, e) => {
    const { name, value } = e.target;
    const updatedForms = [...forms];
    updatedForms[index][name] = value;
    setForms(updatedForms);
  };

  const handleSkillChange = (id, e) => {
    const { name, value } = e.target;
    const updatedSkills = skills.map((skill) =>
      skill.id === id ? { ...skill, [name]: value } : skill
    );
    setSkills(updatedSkills);
  };

  const handleSubmit = async (index) => {
    try {
      await addSkill(forms[index]);
      const updatedForms = [...forms];
      updatedForms.splice(index, 1);
      setForms(updatedForms);
      fetchSkills();
    } catch (err) {
      console.error('Failed to add skill', err);
    }
  };

  const handleUpdate = async (id) => {
    const skillToUpdate = skills.find((skill) => skill.id === id);
    try {
      await updateSkill(id, skillToUpdate);
      setEditingSkillId(null);
      setOriginalSkill(null);
      fetchSkills();
    } catch (err) {
      console.error('Failed to update skill', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSkill(id);
      fetchSkills();
    } catch (err) {
      console.error('Failed to delete skill', err);
    }
  };

  const handleCancelEdit = () => {
    if (originalSkill) {
      const restoredSkills = skills.map((s) =>
        s.id === originalSkill.id ? originalSkill : s
      );
      setSkills(restoredSkills);
    }
    setEditingSkillId(null);
    setOriginalSkill(null);
  };

  const handleCancelForm = (index) => {
    const updatedForms = [...forms];
    updatedForms.splice(index, 1);
    setForms(updatedForms);
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto bg-white shadow-md rounded-2xl space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Skills</h2>

      <button
        onClick={handleAddForm}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition w-full sm:w-auto"
      >
        + Add Skill
      </button>

      {forms.map((form, index) => (
        <div
          key={index}
          className="bg-gray-50 border rounded-xl shadow-sm p-5 space-y-4"
        >
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Skill Name</label>
              <input
                name="skill_name"
                value={form.skill_name}
                onChange={(e) => handleFormChange(index, e)}
                className="w-full p-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Proficiency Level</label>
              <select
                name="proficiency_level"
                value={form.proficiency_level}
                onChange={(e) => handleFormChange(index, e)}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="expert">Expert</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Certification Name</label>
              <input
                name="certification_name"
                value={form.certification_name}
                onChange={(e) => handleFormChange(index, e)}
                className="w-full p-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Certification Issuer</label>
              <input
                name="certification_issuer"
                value={form.certification_issuer}
                onChange={(e) => handleFormChange(index, e)}
                className="w-full p-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Years of Experience</label>
              <input
                name="years_of_experience"
                type="number"
                value={form.years_of_experience}
                onChange={(e) => handleFormChange(index, e)}
                className="w-full p-2 border rounded-md text-sm"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={() => handleSubmit(index)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
            >
              Save
            </button>
            <button
              onClick={() => handleCancelForm(index)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
            >
              Cancel
            </button>
          </div>
        </div>
      ))}

      <h2 className="text-xl font-semibold text-gray-700">Your Skills</h2>

      {skills.length === 0 ? (
        <p className="text-gray-500">No skills found.</p>
      ) : (
        <ul className="space-y-4">
          {skills.map((skill) => (
            <li key={skill.id} className="bg-white border p-5 rounded-xl shadow-sm">
              {editingSkillId === skill.id ? (
                <div className="grid gap-4">
                  <input
                    name="skill_name"
                    value={skill.skill_name}
                    onChange={(e) => handleSkillChange(skill.id, e)}
                    className="w-full p-2 border rounded-md text-sm"
                    placeholder="Skill Name"
                  />
                  <select
                    name="proficiency_level"
                    value={skill.proficiency_level}
                    onChange={(e) => handleSkillChange(skill.id, e)}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="expert">Expert</option>
                  </select>
                  <input
                    name="certification_name"
                    value={skill.certification_name}
                    onChange={(e) => handleSkillChange(skill.id, e)}
                    className="w-full p-2 border rounded-md text-sm"
                    placeholder="Certification Name"
                  />
                  <input
                    name="certification_issuer"
                    value={skill.certification_issuer}
                    onChange={(e) => handleSkillChange(skill.id, e)}
                    className="w-full p-2 border rounded-md text-sm"
                    placeholder="Issuer"
                  />
                  <input
                    type="number"
                    name="years_of_experience"
                    value={skill.years_of_experience}
                    onChange={(e) => handleSkillChange(skill.id, e)}
                    className="w-full p-2 border rounded-md text-sm"
                    placeholder="Years of Experience"
                  />
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      onClick={() => handleUpdate(skill.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Skill:</span> {skill.skill_name}</p>
                    <p><span className="font-medium">Level:</span> {skill.proficiency_level}</p>
                    <p><span className="font-medium">Certification:</span> {skill.certification_name || "—"}</p>
                    <p><span className="font-medium">Issuer:</span> {skill.certification_issuer || "—"}</p>
                    <p><span className="font-medium">Experience:</span> {skill.years_of_experience} year(s)</p>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                    <button
                      onClick={() => {
                        setEditingSkillId(skill.id);
                        setOriginalSkill({ ...skill });
                      }}
                      className= "bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 w-full sm:w-auto"

                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(skill.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 w-full sm:w-auto"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SkillsSection;
